import { QueryFunctionContext } from '@tanstack/react-query';
import { api } from '../services/api';
import { IDataNoPagination } from '../types/shared';

export interface IEmpresaPorUf {
  uf: string | null;
  quantidade: number;
}

export interface IEmpresaPorMunicipio {
  uf: string | null;
  cidade: string | null;
  quantidade: number;
}

export interface IEmpresaPorGrupo {
  grupo: string;
  quantidade: number;
  percentual?: number;
}

export interface IEmpresaTopFaturamento {
  id: number;
  nome: string;
  cnpj: string;
  total_faturamento: number;
}

export interface IPorTipoRegime {
  simples_nacional: number;
  lucro_presumido: number;
  lucro_real: number;
  domesticas: number;
  mei: number;
  imune_isenta: number;
  cnpj_suspenso: number;
  sem_movimento: number;
}

export interface IEmpresaDashTotalizadores {
  total_empresas: number;
  qnt_emp_ativas_periodo: number;
  qnt_emp_ativas_total: number;
  qnt_emp_ativas_percent_periodo: number;
  qnt_emp_ativas_total_percent: number;
  qnt_emp_inativas: number;
  qnt_emp_inativas_percent: number;
  qnt_emp_inativas_periodo: number;
  qnt_emp_inativas_percent_periodo: number;
  qnt_fora_do_acessorias: number;
  qnt_fora_do_acessorias_percent: number;
  qnt_fora_da_dominio: number;
  qnt_fora_da_dominio_percent: number;
  qnt_entradas_periodo: number;
  qnt_entradas_periodo_percent: number;
  qnt_saidas_periodo: number;
  qnt_saidas_periodo_percent: number;
  qnt_com_folha_pagamento: number;
  qnt_com_folha_pagamento_percent: number;
  por_tipo_regime: IPorTipoRegime;
  total_saidas: number;
  total_servicos: number;
  total_outros: number;
  total_faturamento_liquido: number;
  total_faturamento_bruto: number;
  vl_simples_nacional: number;
  total_pis: number;
  total_cofins: number;
  total_csll: number;
  total_irpj: number;
  total_icms: number;
  total_iss: number;
  total_iss_retido: number;
  total_iss_normal: number;
  total_impostos: number;
  por_uf: IEmpresaPorUf[];
  por_municipio: IEmpresaPorMunicipio[];
  por_grupo_empresas: IEmpresaPorGrupo[];
  top_10_faturamento: IEmpresaTopFaturamento[];
}

// Interfaces para a rota /porcentagens
export interface IPorcentagemUf {
  uf: string;
  quantidade: number;
  percentual: number;
}

export interface IPorcentagemMunicipio {
  uf: string;
  cidade: string;
  quantidade: number;
  percentual: number;
}

export interface IPorcentagemRegime {
  regime: string;
  quantidade: number;
  percentual: number;
}

export interface IPorcentagemGrupo {
  grupo: string;
  quantidade: number;
  percentual: number;
}

export interface IEmpresaDashPorcentagens {
  // Percentuais dos cards principais
  qnt_emp_ativas_periodo_percent: number;
  qnt_emp_ativas_total_percent: number;
  qnt_emp_inativas_periodo_percent: number;
  qnt_entradas_periodo_percent: number;
  qnt_saidas_periodo_percent: number;
  qnt_com_folha_pagamento_percent: number;

  // Total de empresas
  total_empresas: number;

  // Agrupamentos com quantidade e percentual
  por_uf: IPorcentagemUf[];
  por_municipio: IPorcentagemMunicipio[];
  por_tipo_regime: IPorcentagemRegime[];
  por_grupo_empresas: IPorcentagemGrupo[];

  // Meta
  meta: {
    date_start: string;
    date_end: string;
  };
}

// Interfaces para dados mensais de faturamento e impostos
export interface IFaturamentoMensal {
  servico: number;
  saida: number;
  outros: number;
  total: number;
}

export interface IImpostosMensal {
  pis: number;
  cofins: number;
  irpj: number;
  csll: number;
  icms: number;
  iss_normal: number;
  iss_retido: number;
  iss_total: number;
  simples_nacional: number;
}

export interface IMesFaturamentoImpostos {
  mes: number;
  mes_nome: string;
  faturamento: IFaturamentoMensal;
  impostos: IImpostosMensal;
}

export interface IEmpresaDashFaturamentoImpostos {
  ano: number;
  meses: IMesFaturamentoImpostos[];
  totais: {
    faturamento: IFaturamentoMensal;
    impostos: IImpostosMensal;
  };
}

export const useEmpresaDash = () => {
  const getTotalizadores = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<IEmpresaDashTotalizadores>> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<IDataNoPagination<IEmpresaDashTotalizadores>>(
      '/api/v1/empresa-dash/totalizadores',
      {
        params: filters,
      },
    );
    return res.data;
  };

  const getPorcentagens = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<IEmpresaDashPorcentagens>> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<IDataNoPagination<IEmpresaDashPorcentagens>>(
      '/api/v1/empresa-dash/porcentagens',
      {
        params: filters,
      },
    );
    return res.data;
  };

  const getFaturamentoImpostos = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<IEmpresaDashFaturamentoImpostos>> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<IDataNoPagination<IEmpresaDashFaturamentoImpostos>>(
      '/api/v1/empresa-dash/faturamento-impostos',
      {
        params: filters,
      },
    );
    return res.data;
  };

  return {
    getTotalizadores,
    getPorcentagens,
    getFaturamentoImpostos,
  };
};
